import React, { useEffect, useMemo, useRef, useState } from "react";
import { Menu, MenuItem } from "@mui/material";
import { DemoIcon, FuturesIcon } from "@components/library";
import { AnimatedCounter } from "react-animated-counter";
import { changeWallet, getWallets, updateWalletEvent } from "@actions";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "@helpers/translate";
import ArrowDownIcon from "@assets/icons/ArrowDownIcon";
import {
  WALLET_DEPOSIT,
  WALLET_TYPE,
  WALLET_WITHDRAWAL,
} from "../../../../constants";
import Collapse from "@mui/material/Collapse";
import { Link } from "react-router-dom";
import "./Mode.sass";

// Один рахунок на режим: Real account і Demo account. Старі real/demo не показуємо.
const walletOrder = {
  [WALLET_TYPE.REAL_FUTURES]: 1,
  [WALLET_TYPE.DEMO_FUTURES]: 2,
};

const isSafariBrowser = () => {
  if (typeof window === "undefined") return false;

  const userAgent = window.navigator.userAgent;

  return /^((?!chrome|android|crios|fxios|edgios|opr|opera).)*safari/i.test(
    userAgent,
  );
};

const formatBalance = (value) => Number(value || 0).toFixed(2);

const WalletBalance = ({ value, fontSize }) => {
  const isSafari = useMemo(() => isSafariBrowser(), []);

  if (isSafari) {
    return (
      <span
        style={{
          color: "#ffffff",
          fontSize,
          fontFamily: "inherit",
          fontWeight: "inherit",
          lineHeight: "inherit",
        }}
      >
        {formatBalance(value)}
      </span>
    );
  }

  return (
    <AnimatedCounter
      value={Number(value || 0)}
      fontSize={fontSize}
      color="#ffffff"
    />
  );
};

const Mode = () => {
  const dispatch = useDispatch();
  const { active_wallet, wallets, loading } = useSelector(
    (state) => state.wallet,
  );
  const { __ } = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);
  const buttonRef = useRef(null);
  // const {pathname} = useLocation();

  const handleOpen = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => {
    setAnchorEl(null);
    if (buttonRef.current) {
      buttonRef.current.blur();
    }
  };

  const handleSelectChange = async (data) => {
    try {
      const wallet = wallets.find((item) => item.type === data.type);
      await dispatch(changeWallet(wallet));
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    dispatch(getWallets());
    dispatch(updateWalletEvent());
  }, []);

  const getWalletName = (type) => {
    switch (type) {
      case WALLET_TYPE.DEMO_FUTURES:
        return __("common.demo_futures");
      case WALLET_TYPE.REAL_FUTURES:
        return __("common.real_futures");
      default:
        return "";
    }
  };

  const getWalletIcon = (type) => {
    switch (type) {
      case WALLET_TYPE.DEMO_FUTURES:
        return DemoIcon();
      case WALLET_TYPE.REAL_FUTURES:
        return FuturesIcon();
      default:
        return "";
    }
  };

  if (!active_wallet && loading) return <></>;

  if (!active_wallet) return <></>;

  return (
    <div className={`mode-select${anchorEl ? " mode-select--opened" : ""}`}>
      <button
        type="button"
        className="mode-select__btn"
        onClick={handleOpen}
        ref={buttonRef}
      >
        <div className="mode-item">
          <span className="mode-item__icon">
            {getWalletIcon(active_wallet.type)}
          </span>
          <div className="mode-item__content">
            <p className="mode-item__name">
              {getWalletName(active_wallet.type)}
            </p>
            <h5 className="mode-item__value">
              <WalletBalance value={active_wallet.balance} fontSize="14px" />
              <span className="mode-item__value-currency">USDT</span>
              <ArrowDownIcon className="mode-select__btn-arrow" />
            </h5>
          </div>
        </div>
      </button>
      <Menu
        className="mode-select-menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        disableRestoreFocus
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
      >
        {wallets
          .filter((wallet) => walletOrder[wallet.type])
          .sort(
            (a, b) => walletOrder[a.type] - walletOrder[b.type],
          )
          .map((wallet) => {
            return (
              <MenuItem
                selected={active_wallet.type === wallet.type}
                onClick={() => handleSelectChange(wallet)}
                key={wallet.id}
                value={wallet.type}
              >
                <div className="mode-item-wrapper">
                  <div className="mode-item">
                    <span className="mode-item__icon">
                      {getWalletIcon(wallet.type)}
                    </span>
                    <div className="mode-item__content">
                      <p className="mode-item__name">
                        {getWalletName(wallet.type)}
                      </p>
                      <h5 className="mode-item__value">
                        <WalletBalance value={wallet.balance} fontSize="16px" />
                        <span className="mode-item__value-currency">USDT</span>
                      </h5>
                    </div>
                  </div>

                  {/* ================== ВИПРАВЛЕНО: Переносимо кнопки до "Futures account" ================== */}
                  {wallet.type === WALLET_TYPE.REAL_FUTURES && (
                    <Collapse in={active_wallet.type === wallet.type}>
                      <div className="mode-item-content">
                        <Link
                          to={WALLET_DEPOSIT}
                          onClick={(e) => {
                            e.stopPropagation();
                          }}
                          className="mode-item-content__btn"
                        >
                          {__("wallet.top_up")}
                        </Link>
                        <Link
                          to={WALLET_WITHDRAWAL}
                          onClick={(e) => e.stopPropagation()}
                          className="mode-item-content__deposit"
                        >
                          <svg
                            width="12"
                            height="12"
                            viewBox="0 0 12 12"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M10.8445 6.5045C10.8445 6.503 10.8455 6.5015 10.8455 6.5C10.8455 6.4955 10.843 6.492 10.843 6.4875C10.837 6.452 10.83 6.4165 10.8215 6.381C10.8175 6.369 10.8155 6.357 10.8105 6.3455C10.7865 6.252 10.7625 6.159 10.724 6.069L9.7345 3.761C9.484 3.1765 9.24749 2.6245 8.01099 2.6245C7.80399 2.6245 7.63599 2.7925 7.63599 2.9995C7.63599 3.2065 7.80399 3.3745 8.01099 3.3745C8.75299 3.3745 8.82054 3.5325 9.04504 4.0565L9.93152 6.1245H8.52246C7.89746 6.1245 7.31806 6.47699 6.97156 7.06799C6.77106 7.41099 6.3985 7.6245 6 7.6245C5.6015 7.6245 5.22894 7.41101 5.02844 7.0675C4.68244 6.47701 4.10254 6.1245 3.47754 6.1245H2.06848L2.95496 4.0565C3.17946 3.5325 3.24701 3.3745 3.98901 3.3745C4.19601 3.3745 4.36401 3.2065 4.36401 2.9995C4.36401 2.7925 4.19601 2.6245 3.98901 2.6245C2.75201 2.6245 2.516 3.1765 2.2655 3.761L1.276 6.0695C1.2375 6.159 1.21345 6.252 1.18945 6.345C1.18445 6.3565 1.18247 6.369 1.17847 6.381C1.16997 6.416 1.16348 6.4515 1.15698 6.487C1.15698 6.4915 1.15454 6.495 1.15454 6.4995C1.15454 6.501 1.15552 6.502 1.15552 6.504C1.13902 6.604 1.125 6.705 1.125 6.8075V9C1.125 10.209 1.791 10.875 3 10.875H9C10.209 10.875 10.875 10.209 10.875 9V6.808C10.875 6.705 10.861 6.6045 10.8445 6.5045ZM9 10.125H3C2.2115 10.125 1.875 9.7885 1.875 9V6.875H3.47754C3.83354 6.875 4.17098 7.08901 4.38098 7.44701C4.71548 8.01951 5.336 8.375 6 8.375C6.664 8.375 7.28452 8.01949 7.61902 7.44749C7.82902 7.08899 8.16646 6.875 8.52246 6.875H10.125V9C10.125 9.7885 9.7885 10.125 9 10.125ZM4.73499 4.265C4.58849 4.1185 4.58849 3.881 4.73499 3.7345C4.88149 3.588 5.119 3.588 5.2655 3.7345L5.62549 4.0945V1C5.62549 0.793 5.79349 0.625 6.00049 0.625C6.20749 0.625 6.37549 0.793 6.37549 1V4.0945L6.73547 3.7345C6.88197 3.588 7.11949 3.588 7.26599 3.7345C7.41249 3.881 7.41249 4.1185 7.26599 4.265L6.26599 5.265C6.23149 5.2995 6.19004 5.32699 6.14404 5.34599C6.09804 5.36499 6.04949 5.375 6.00049 5.375C5.95149 5.375 5.90306 5.36499 5.85706 5.34599C5.81106 5.32699 5.76949 5.2995 5.73499 5.265L4.73499 4.265Z"
                              fill="#FF6C22"
                            />
                          </svg>
                        </Link>
                      </div>
                    </Collapse>
                  )}
                  {/* ======================================================================================== */}
                </div>
              </MenuItem>
            );
          })}
      </Menu>
    </div>
  );
};

export default Mode;
